import React, {useState, forwardRef, useImperativeHandle, useEffect} from 'react';
import {labels} from '../../../../../common/utils/utils';
import Button from '../../../../../common/components/Button/Button';
import {MotifIcon, MotifTooltip} from '@common/MotifWrapperComponents/motifCompat';
import {IconoirPlusCircle} from '@ey-xd/motif-icon';
import UserSearch from '../../../../../common/components/Search/usersearch';
import {useSelector} from 'react-redux';
import {isEmpty} from 'lodash';
import {IconoirXmarkCircle} from '@ey-xd/motif-icon';

const AddDelegate = forwardRef((props, ref) => {
	const {setLoading, children, setIsDisable} = props;
	const getId = () => new Date().getTime();
	const [userList, setUserList] = useState([]);

	const user = useSelector((store) => store.user);

	useImperativeHandle(ref, () => ({
		validate() {
			return validateDelegateUser();
		},
		getUsers() {
			return getFormattedUsers();
		},
		resetUserList() {
			setUserList([]);
		}
	}));

	useEffect(() => {
		setIsDisable(userList.length === 0);
	}, [userList]);

	const getFormattedUsers = () => {
		return userList.map((user) => user.emailAddress);
	};

	const checkIfDuplicateUser = (userId, skipUserList = false) => {
		let userListIdx = -1;
		if (!skipUserList) {
			userListIdx = userList.findIndex((u) => u.userId === userId);
		}
		const delegatedToIdx = user.delegatedTo.findIndex((u) => u.userId === userId);
		return userListIdx > -1 || delegatedToIdx > -1;
	};

	const validateDelegateUser = () => {
		const mappedList = userList.map((user) => {
			if (isEmpty(user.userId)) {
				return {
					...user,
					isError: true,
					errorMessage: labels.informationRequired
				};
			}
			const isDuplicate = checkIfDuplicateUser(user.userId, true);
			if (isDuplicate) {
				return {
					...user,
					isError: true,
					errorMessage: labels.duplicateUser
				};
			}
			return {...user};
		});
		setUserList(mappedList);
		return mappedList.length === 0 ? true : mappedList.findIndex((user) => user.isError) > -1;
	};

	const onUserClear = (id) => {
		const clonedList = [...userList];
		const idx = clonedList.findIndex((u) => u.id === id);
		if (idx > -1) {
			clonedList[idx] = {
				...clonedList[idx],
				firstName: '',
				lastName: '',
				emailAddress: '',
				userId: '',
				isError: false,
				errorMessage: ''
			};
			setUserList(clonedList);
		}
	};

	const onUserSelectionChange = (user, id) => {
		const clonedList = [...userList];
		const isDuplicate = checkIfDuplicateUser(user.allProps.gui);
		const idx = clonedList.findIndex((u) => u.id === id);
		if (isDuplicate) {
			clonedList[idx] = {
				...clonedList[idx],
				firstName: user.allProps.firstName,
				lastName: user.allProps.lastName,
				emailAddress: user.allProps.email,
				userId: user.allProps.gui,
				isError: true,
				errorMessage: labels.duplicateUser
			};
		} else {
			clonedList[idx] = {
				...clonedList[idx],
				firstName: user.allProps.firstName,
				lastName: user.allProps.lastName,
				emailAddress: user.allProps.email,
				userId: user.allProps.gui,
				isError: false,
				errorMessage: ''
			};
		}
		setUserList(clonedList);
	};

	const addDelegateRow = () => {
		const delegatedUserList = [
			...userList,
			{
				id: getId(),
				firstName: '',
				lastName: '',
				emailAddress: '',
				userId: '',
				isError: false,
				errorMessage: ''
			}
		];
		setUserList(delegatedUserList);
		if (delegatedUserList.length === 1) {
			setIsDisable(false);
		}
	};

	const onUserRemove = (id) => {
		const clonedList = [...userList];
		const idx = clonedList.findIndex((u) => u.id === id);
		if (idx > -1) {
			clonedList.splice(idx, 1);
			setUserList(clonedList);
		}
		if (clonedList.length === 0) {
			setIsDisable(true);
		}
	};

	return (
		<section className="add-delegate-list">
			<Button onClick={addDelegateRow} variant="text-alt" className="addDelegateBtn" label={labels.addAnother}>
				<MotifIcon src={IconoirPlusCircle} />
			</Button>
			<section>{children}</section>
			<ul>
				{userList.map((user) => {
					return (
						<li key={user.id}>
							<UserSearch
								setLoading={setLoading}
								user={user}
								onUserClear={(e) => onUserClear(user.id)}
								onUserSelectionChange={(e) => onUserSelectionChange(e, user.id)}
								maxSearchLength={50}
								showNameAsValue
								isClearable
							/>
							{
								<MotifTooltip
									id={`remove-${user.id}`}
									trigger={
										<span onClick={() => onUserRemove(user.id)} className="remove-delegate-button">
											<MotifIcon src={IconoirXmarkCircle} />
										</span>
									}
									placement="right"
									variant="alt"
									hideCloseButton
									contentClassName="hideTooltipClose"
								>
									{labels.remove}
								</MotifTooltip>
							}
						</li>
					);
				})}
			</ul>
		</section>
	);
});

AddDelegate.displayName = 'AddDelegate';

export default AddDelegate;
